import React from 'react';
import { AlertTriangle, AlertCircle, CheckSquare, FileText, Target, Mic } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

import InfoIcon from './InfoIcon';
import ScopeDot from './ScopeDot';

const UrgentActionsCompact = ({ actions, count, navigate }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'overdue_task': return <CheckSquare className="w-3.5 h-3.5 text-red-500" />;
      case 'undocumented_activity': return <FileText className="w-3.5 h-3.5 text-orange-500" />;
      case 'stale_opportunity': return <Target className="w-3.5 h-3.5 text-yellow-500" />;
      case 'pending_voice_note': return <Mic className="w-3.5 h-3.5 text-blue-500" />;
      default: return <AlertCircle className="w-3.5 h-3.5 text-gray-500" />;
    }
  };
  const hint = {
    overdue_task: 'Marca hecha o reprograma',
    undocumented_activity: 'Rellena el resumen',
    stale_opportunity: 'Avanza etapa o registra actividad',
    pending_voice_note: 'Confirma procesamiento',
  };
  const handle = (a) => {
    switch (a.type) {
      case 'overdue_task': navigate(`/tasks?id=${a.id}`); break;
      case 'undocumented_activity': navigate(`/activities?id=${a.id}`); break;
      case 'stale_opportunity': navigate(`/pipeline/${a.id}`); break;
      case 'pending_voice_note': navigate(`/voice-notes/${a.id}`); break;
      default: break;
    }
  };
  const topFive = (actions || []).slice(0, 5);
  return (
    <Card className={count === 0 ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/30'} data-testid="urgent-actions-compact">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <AlertTriangle className={`w-4 h-4 ${count === 0 ? 'text-green-600' : 'text-red-600'}`} />
            Urgentes
            <InfoIcon text="Oportunidades que llevan demasiados días sin movimiento y necesitan avanzar etapa o registrar actividad." testid="info-urgent-actions" ariaLabel="Información sobre Urgentes" />
          </span>
          {count > 0 && <Badge variant="destructive" className="text-xs">{count}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {topFive.length === 0 ? (
          <p className="text-xs text-green-700">Todo en orden.</p>
        ) : (
          <>
            <ScrollArea className="h-[140px]">
              <div className="space-y-0.5">
                {topFive.map((a, idx) => (
                  <button
                    key={`${a.type}-${a.id}-${idx}`}
                    type="button"
                    onClick={() => handle(a)}
                    className="w-full flex items-start gap-2 px-1.5 py-1 rounded hover:bg-red-100/50 text-left"
                    data-testid={`urgent-action-${idx}`}
                  >
                    {getIcon(a.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate flex items-center">
                        <ScopeDot scope={a.scope} />
                        <span className="truncate">{a.title}</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground italic truncate">
                        → {hint[a.type] || '-'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
            {count > topFive.length && (
              <button
                type="button"
                onClick={() => navigate('/tasks')}
                className="text-[10px] text-muted-foreground hover:text-primary mt-1 w-full text-right"
              >
                Ver todos ({count}) →
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UrgentActionsCompact;
